import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Button } from '@src/common/components/button/Button';
import { Card } from '@src/common/components/Card';
import { Divider } from '@src/common/components/Divider';
import { FormTextInput } from '@src/common/components/FormTextInput';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { DocumentFormFileUpload } from '@src/documents/components/DocumentFormFileUpload';
import { FormFileModes } from '@src/documents/components/FormFileModes';
import { getLastPartOfPath } from '@src/documents/hooks/useFindAllDocumentsForOrder';
import type { OrderPermitApplication } from '@src/orders/domain/Order';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import {
  ConfirmationNumberField,
  DocumentsFields,
} from '@src/permits/completePermit/fields/ProcessWithJurisdictionPermitFields';
import { ProcessWithJurisdictionPermitSchema } from '@src/permits/completePermit/fields/ProcessWithJurisdictionPermitFields';
import { useSavePermitSubmissionAsDraft } from '@src/permits/hooks/useSavePermitSubmissionAsDraft';
import type { Control, FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type ProcessWithJurisdictionDocument = {
  name: string;
  documentId: string;
  sendToClient: boolean;
  showToClient?: boolean;
};

interface Props {
  orderPermitApplication: OrderPermitApplication;
  orderId: string;
  className?: string;
  canSaveProgress: boolean;
  onSubmit: (data: {
    confirmationNumber: string;
    documents?: ProcessWithJurisdictionDocument[];
  }) => void;
}

export const SubmitPermitForm = ({
  orderPermitApplication,
  orderId,
  className,
  onSubmit,
  canSaveProgress,
}: Props) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();
  const { navigateToOrderDetails } = useNavigateToOrderDetails();
  const { refreshOrders } = useRefreshOrders();

  const redirectToOrderDetail = () => {
    void navigateToOrderDetails({ orderId });
  };

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    watch,
  } = useForm({
    resolver: yupResolver(ProcessWithJurisdictionPermitSchema),
    defaultValues: {
      [ConfirmationNumberField.fieldName]:
        orderPermitApplication?.submission?.confirmationNumber,
      [DocumentsFields.fieldName]:
        orderPermitApplication?.application?.documents?.map((document) => ({
          documentId: document.documentId,
          name: getLastPartOfPath(document.key),
          sendToClient: document.sendToClient,
          showToClient: document.showToClient,
        })),
    },
  });

  const stateApplicationNumber = watch('confirmationNumber');

  const { savePermitSubmissionAsDraft } = useSavePermitSubmissionAsDraft({
    permitApplicationId: orderPermitApplication.application.id,
    onSuccess: () => {
      showSuccessToast(t('submitConfirmationNumber.success'));
      refreshOrders();
      redirectToOrderDetail();
    },
    onError: () => showErrorToast(t('submitConfirmationNumber.error')),
  });

  const saveProgress = () => {
    const { confirmationNumber: confirmationNumber, documents } = getValues();
    savePermitSubmissionAsDraft({ confirmationNumber, documents });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={form}>
      <Card className={className} style={{ gap: theme.spacing['6'] }}>
        <Typography variant="subtitle2" className={subtitle}>
          {t('permit.submissionTitle')}
        </Typography>

        <Stack style={{ width: '50%' }}>
          <FormTextInput
            size="medium"
            register={register}
            errors={errors}
            fieldName="confirmationNumber"
          />
        </Stack>

        {orderPermitApplication.submission?.submissionDate && (
          <Typography variant="body2">
            {t('permit.submissionInfo', {
              submissionDate:
                orderPermitApplication.submission.submissionDate.format(),
            })}
          </Typography>
        )}
      </Card>

      <Card className={className} style={{ gap: theme.spacing['6'] }}>
        <Typography variant="subtitle2" className={subtitle}>
          {t('jurisdictionDocuments')}
        </Typography>

        <DocumentFormFileUpload
          orderId={orderId}
          permitApplicationId={orderPermitApplication.application.id}
          control={control as unknown as Control<FieldValues>}
          mode={FormFileModes.MAKE_VISIBLE_TO_CLIENT}
        />
      </Card>

      <Divider />

      <Stack direction="row" justifyContent="space-between">
        <Button
          type="button"
          className={button}
          onClick={redirectToOrderDetail}
        >
          <CloseIcon style={{ marginRight: theme.spacing['2'] }} />
          {t('cancel')}
        </Button>

        <Stack direction="row" gap={theme.spacing['2']}>
          {canSaveProgress && (
            <Button
              type="button"
              disabled={!stateApplicationNumber}
              onClick={saveProgress}
              variant="outlined"
              startIcon={<SaveOutlinedIcon />}
            >
              {t('saveProgress')}
            </Button>
          )}

          <Button
            type="submit"
            variant="contained"
            className={button}
            disabled={!stateApplicationNumber}
            startIcon={<CheckIcon />}
          >
            {t('completeWithJurisdiction')}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

const subtitle = css(() => ({
  borderBottom: '1px solid',
  borderColor: `${theme.palette.surface.outlineVariant}`,
  paddingBottom: `${theme.spacing['2']}`,
}));

const form = css(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing['8'],
}));

const button = css({
  padding: '0 18px',
  height: '40px',
});
